import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Renders the typed nodes an instancing scene adds inside a GLB, each under the GLB object its
 * parent path names. Godot adds such a node to the node that path resolves to, then moves it to its
 * `index=` among that node's children (`packed_scene.cpp:157-165`, `:540-545`), so it takes that
 * object's transform and visibility, and that place in tree order. The instancing node's scene
 * authored it, so it sits at that scene's level, not the GLB's.
 */
import { useMemo } from 'react';
import { joinPath } from '../../../utils/nodePath';
import { InstancedScenePathsProvider } from '../../contexts/InstancedScenePathsContext';
import { DispatchedNode } from '../../NodeDispatcher';
import { AttachedToObject, ParentTypeProvider } from '../../parentSpaceScope';
import { VisibilityParentProvider } from '../../visibilityRange/VisibilityParentContext';
import { childOrders, TreeOrderProvider, useTreeOrder, } from '../../contexts/TreeOrderContext';
import { glbChildrenBelow, glbSubtreeEnd } from './glbHierarchy';
import { isApplicableGlbOverride, resolveGlbAttachments } from './glbNodeOverrides';
import { warn } from '../../../logger';
/** A GLB object is a Node3D in Godot, so a child of another family escapes it as it escapes a Node3D. */
const GLB_OBJECT_TYPE = 'Node3D';
export function GlbAttachedChildren({ links, root }) {
    const rootOrder = useTreeOrder();
    const attached = useMemo(() => attachedChildren(links, root, rootOrder), [links, root, rootOrder]);
    return attached.map(({ node, path, target, order, visibilityParent }) => (_jsx(AttachedToObject, { target: target, children: _jsx(ParentTypeProvider, { value: GLB_OBJECT_TYPE, children: _jsx(VisibilityParentProvider, { value: visibilityParent, children: _jsx(TreeOrderProvider, { order: order, children: _jsx(InstancedScenePathsProvider, { paths: root.hostScenePaths, children: _jsx(DispatchedNode, { node: node, path: path }) }) }) }) }) }, path)));
}
/**
 * Each typed node of `overrides` with the GLB object its parent path resolves to, in authored
 * order. A path that names no object drops its node, with a warning.
 */
function attachedChildren(links, root, rootOrder) {
    // The common GLB has none, so nothing below runs for it. A GLB opened as the scene has no
    // instancing node to address into it.
    if (root.path === null || root.overrides.every(isApplicableGlbOverride))
        return [];
    const rootPath = root.path;
    const entries = links.map((link) => link.entry);
    const { attached: placed, rest } = resolveGlbAttachments(entries, root.overrides);
    warnUnattached(rest);
    const orders = attachedOrders(entries, childOrders(rootOrder, entries.length), placed);
    return placed.map(({ node, target, host }) => ({
        node,
        path: joinPath(joinPath(rootPath, entries[target].relPath), node.name),
        target: host,
        order: orders.get(node),
        visibilityParent: links[target].place.parentPath,
    }));
}
/** Warns for each typed node of `unattached`, whose parent path names no GLB object. */
function warnUnattached(unattached) {
    for (const node of unattached) {
        if (isApplicableGlbOverride(node))
            continue;
        warn(`[GlbAttachedChildren] "${node.instanceSubPath}" matches nothing in the loaded GLB, so "${node.name}" is dropped`);
    }
}
/**
 * The tree order of each placed node, among its target's children as `glbChildrenBelow` leaves them.
 * `entryOrders` gives each GLB object its place by its pre-order index, so a node takes a place
 * just after the subtree of the sibling object before it, or just after the target when it is first.
 */
function attachedOrders(entries, entryOrders, placed) {
    const orders = new Map();
    for (const [target, children] of glbChildrenBelow(entries, placed)) {
        let after = target;
        let sinceObject = 0;
        for (const child of children) {
            if ('entry' in child) {
                after = glbSubtreeEnd(entries, child.entry) - 1;
                sinceObject = 0;
            }
            else {
                orders.set(child, [...entryOrders[after], sinceObject++]);
            }
        }
    }
    return orders;
}
//# sourceMappingURL=GlbAttachedChildren.js.map